import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import useThemeStore from "../store/themeStore";
import useTokenStore from "../store/store";
import { ThemeProps } from "../utils/theme";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import Footer from "../components/headerFooter/Footer";
import { completeOAuthRegistration } from "../utils/user";

function OAuthTermsRequired() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setToken = useTokenStore((state) => state.setToken);
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingToken = searchParams.get("pendingToken");
  const provider = searchParams.get("provider") || "social";
  const email = searchParams.get("email") || "";

  // Redirect if no pending token
  useEffect(() => {
    if (!pendingToken) {
      navigate("/login?error=missing_token");
    }
  }, [pendingToken, navigate]);

  const getProviderName = () => {
    if (provider === "google") return "Google";
    if (provider === "facebook") return "Facebook";
    return "social login";
  };

  const handleAccept = async () => {
    if (!termsAccepted || !pendingToken) return;

    setLoading(true);
    setError(null);

    try {
      const response = await completeOAuthRegistration(pendingToken);

      if (response.status === 200 || response.status === 201) {
        // Store token and redirect
        setToken(response);
        navigate("/listing");
      } else {
        setError(response.message || "Failed to complete registration");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Complete OAuth error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/login");
  };

  const color1 = getThemes?.color1 || "#1e1b4b";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        position: "relative",
        overflow: "auto",
      }}
    >
      <Helmet>
        <title>Accept Terms | {getThemes && getThemes.name}</title>
      </Helmet>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "60px 60px",
          zIndex: -1,
        }}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          }}
        >
          <h2
            style={{
              margin: "0 0 16px 0",
              fontSize: "24px",
              fontWeight: "600",
              color: "#333",
              textAlign: "center",
            }}
          >
            Almost there!
          </h2>

          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              color: "#666",
              lineHeight: "1.5",
              textAlign: "center",
            }}
          >
            You're signing up with {getProviderName()}
          </p>

          {email && (
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: "14px",
                color: "#333",
                fontWeight: "500",
                textAlign: "center",
              }}
            >
              {email}
            </p>
          )}

          <p
            style={{
              margin: "0 0 24px 0",
              fontSize: "14px",
              color: "#666",
              lineHeight: "1.5",
            }}
          >
            Before we create your account, please review and accept our Terms & Conditions and Privacy Policy.
          </p>

          {error && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                padding: "12px",
                marginBottom: "20px",
                color: "#dc2626",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{
                marginTop: "2px",
                width: "18px",
                height: "18px",
                cursor: "pointer",
                accentColor: color1,
              }}
            />
            <label
              htmlFor="terms-checkbox"
              style={{
                fontSize: "14px",
                color: "#333",
                cursor: "pointer",
                lineHeight: "1.4",
              }}
            >
              I agree to the{" "}
              <a
                href="/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: color1, textDecoration: "underline" }}
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: color1, textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "12px 24px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                backgroundColor: "#fff",
                color: "#666",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!termsAccepted || loading}
              style={{
                padding: "12px 24px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: termsAccepted && !loading ? color1 : "#ccc",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "500",
                cursor: termsAccepted && !loading ? "pointer" : "not-allowed",
                opacity: termsAccepted && !loading ? 1 : 0.7,
                minWidth: "150px",
              }}
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default OAuthTermsRequired;
